import { isServer } from '@vue-storefront/core/helpers';
import config from 'config';

// Real-user monitoring for the storefront: Core Web Vitals (LCP, INP, CLS, FCP,
// TTFB) + JS errors. Browser-only. Beacons go in Loki push format to Alloy's
// loki.source.api endpoint (same-origin `/rum` by default, so no CORS).
//
// web-vitals is loaded at RUNTIME via a <script> tag (UMD/IIFE, `webVitals`
// global) rather than an npm import on purpose: this storefront builds on
// TypeScript 3.4, and web-vitals' npm typings use `import type` (TS 3.8+) which
// breaks the ts-loader build. Loading the prebuilt browser bundle sidesteps the
// bundler/type-checker entirely. (Faro was also ruled out — its OTel deps need
// Node >=18 and the build runs on Node 10.)
//
// Config is read LAZILY (after hydration): the webpack `config` alias inlines
// the BUILD-TIME snapshot (core/build/config.json, rum.enabled=false); the real
// per-env values arrive at runtime via dynamicConfigReload, which client-entry.ts
// merges into the `config` singleton AFTER this module has evaluated. Reading at
// import time would always see enabled:false. See git history for the full story.
//
// ERROR CAPTURE: Vue 2 catches component render/lifecycle/watcher errors and
// routes them to Vue.config.errorHandler (see themes/korzyna/index.js) — they
// never reach window.onerror. So that handler calls captureException() below.
// captureException() is also fed by the window error/rejection listeners, which
// are registered immediately (not at `load`) and buffer until RUM initialises,
// so early/hydration errors aren't lost.

const MAX_QUEUE = 50;
const queue = [];
let send = null; // set once RUM is known to be enabled + initialised

const pageOf = () => (window.location && window.location.pathname) || '/';

// Public reporter. Safe to call anytime and on the server (no-op). Buffers
// (bounded) until init decides whether RUM is enabled.
export function captureException (payload) {
  if (isServer) return;
  if (send) { send(payload); return; }
  if (queue.length < MAX_QUEUE) queue.push(payload);
}

// Uncaught errors + unhandled rejections fire on window regardless of RUM init;
// register early and route through the (buffered) reporter.
if (!isServer) {
  window.addEventListener('error', (e) => captureException({
    message: e.message,
    source: e.filename,
    line: e.lineno,
    col: e.colno,
    stack: e.error && e.error.stack ? String(e.error.stack).slice(0, 2000) : undefined,
    page: pageOf()
  }));
  window.addEventListener('unhandledrejection', (e) => captureException({
    message: 'unhandledrejection: ' + (e.reason && e.reason.message ? e.reason.message : String(e.reason)),
    stack: e.reason && e.reason.stack ? String(e.reason.stack).slice(0, 2000) : undefined,
    page: pageOf()
  }));
}

function startRum () {
  const rum = config.rum || {};
  const enabled = rum.enabled === true || rum.enabled === 'true';
  if (!enabled) {
    // RUM off: make the reporter a no-op and drop anything buffered.
    send = () => {};
    queue.length = 0;
    return;
  }

  const url = rum.collectorUrl || '/rum/loki/api/v1/push';
  const libUrl = rum.libUrl || 'https://unpkg.com/web-vitals@3/dist/web-vitals.iife.js';
  const labels = {
    app: rum.appName || 'storefront',
    deployment_environment: rum.environment || 'unknown',
    app_version: rum.version || 'dev'
  };

  const post = (kind, payload) => {
    try {
      const body = JSON.stringify({
        streams: [{
          stream: Object.assign({ kind }, labels),
          values: [[String(Date.now()) + '000000', JSON.stringify(payload)]]
        }]
      });
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'application/json' } }).catch(() => { /* never let RUM break the page */ });
      }
    } catch (e) { /* never let RUM break the page */ }
  };

  // Activate the reporter (exceptions are always sent — not sampled) and flush
  // anything captured before init.
  send = (payload) => post('exception', payload);
  while (queue.length) send(queue.shift());

  const sampled = () => {
    const rate = rum.sampleRate != null ? Number(rum.sampleRate) : 1;
    return rate >= 1 || Math.random() < rate;
  };

  // Web vitals are sampled (they're high-volume); errors above are not.
  if (sampled()) {
    const s = document.createElement('script');
    s.src = libUrl;
    s.async = true;
    s.onload = () => {
      const wv = window.webVitals;
      if (!wv) return;
      const report = (m) => post('web-vital', {
        name: m.name,
        value: m.value,
        rating: m.rating,
        id: m.id,
        navigationType: m.navigationType,
        page: pageOf()
      });
      ['onLCP', 'onINP', 'onCLS', 'onFCP', 'onTTFB'].forEach((fn) => {
        if (typeof wv[fn] === 'function') wv[fn](report);
      });
    };
    s.onerror = () => { /* lib failed to load; ignore */ };
    document.head.appendChild(s);
  }
}

if (!isServer) {
  // Defer until after client-entry.ts merges the runtime config into `config`.
  if (document.readyState === 'complete') {
    setTimeout(startRum, 0);
  } else {
    window.addEventListener('load', startRum, { once: true });
  }
}
